import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useLicense, PROFESSIONAL_ONLY_PAGES } from "../../contexts/LicenseContext";
import { usePermissions } from "../../contexts/PermissionContext";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Receipt,
  Users,
  Truck,
  UserCog,
  BarChart3,
  Settings,
  AlertTriangle,
  Wallet,
  LogOut,
  X,
  CreditCard,
  HandCoins,
  Calculator,
  Shield,
  Info,
  Activity,
  Banknote,
  Crown,
  Palette,
  Megaphone,
  RotateCcw,
  Tag,
  HelpCircle,
  Database,
  Lock,
  Key,
  UsersRound,
  Store,
  FileText,
  History,
  PauseCircle,
  Bell,
  BookOpen,
  Handshake,
  UserCircle,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Button } from "../ui/button";

export const Sidebar = ({ isOpen, onClose }) => {
  const { logout, user, isActive, licenseType, isWhiteLabel, getLicenseTypeName } = useAuth();
  const { isRegular, isProfessional, promptUpgrade, isOwner } = useLicense();
  const { isOwner: isShopOwner, can } = usePermissions();
  const navigate = useNavigate();

  // عداد الفواتير المعلقة (badge ديناميكي)
  const [heldCount, setHeldCount] = useState(0);
  // ✨ قسم "الحساب" قابل للطي
  const [accountOpen, setAccountOpen] = useState(false);
  useEffect(() => {
    let timer = null;
    let mounted = true;
    const fetchCount = async () => {
      try {
        const API = `${window.location.origin}/api`;
        const res = await axios.get(`${API}/held-invoices/stats/count`);
        if (mounted) setHeldCount(res.data?.count || 0);
      } catch {
        if (mounted) setHeldCount(0);
      }
    };
    fetchCount();
    timer = setInterval(fetchCount, 30000); // كل 30 ثانية
    // refresh عند فتح/إغلاق الـ Sidebar
    return () => { mounted = false; if (timer) clearInterval(timer); };
  }, [isOpen]);

  // Path → required permission. Owner sees everything; employees only see allowed pages.
  const PATH_PERMISSIONS = {
    "/pos": "pos_access",
    "/inventory": "view_inventory",
    "/customers": "view_customers",
    "/suppliers": "view_suppliers",
    "/expenses": "view_expenses",
    "/reports": "view_reports",
    "/sales": "view_sales",
    "/purchases": "view_inventory",
    "/customer-debts": "manage_customer_debts",
    "/supplier-debts": "manage_supplier_debts",
    "/employees": "view_employees",
    "/damages": "view_inventory",
    "/returns": "pos_refund",
    "/expiry-alerts": "view_inventory",
    "/backup": "manage_backup",
    "/roles": "manage_roles",
    "/labels-settings": "view_settings",
    "/store-settings": "view_settings",
    "/account-statement": "view_financial_reports",
    "/audit-log": "view_logs",
    "/team": "manage_roles", // ✨ إدارة الموظفين Multi-User (مالك فقط)
    "/cash-register": "manage_cash_register",
    "/installments": "view_sales",
    "/installment-management": "view_sales",
    "/installment-reports": "view_reports",
    "/theme": "view_settings",
    "/settings": "view_settings",
    "/whitelabel-settings": "view_settings",
    "/license-admin": "full_admin",
    "/activation-history": "full_admin",
    "/held-invoices": "view_sales",
    "/reminders": "view_sales",
    "/multi-vouchers": "view_financial_reports",
    "/overdue-report": "view_sales",
  };

  const isAllowedByPermission = (path) => {
    if (isShopOwner) return true; // owner always sees everything
    const required = PATH_PERMISSIONS[path];
    if (!required) return true; // no restriction defined
    return can(required);
  };

  // Check if user is admin - التحقق من دور المستخدم أو البريد
  const userEmailLower = (user?.email || "").toLowerCase().trim();
  const userRoleLower = (user?.role || "").toLowerCase().trim();
  
  const isAdmin = userRoleLower === "admin" || 
                  userRoleLower === "super_admin" ||
                  userEmailLower === "admin@hesabaty.com" || 
                  userEmailLower === "admin@nazario.com" ||
                  userEmailLower === "superadmin@nazario.com" || 
                  userEmailLower === "manager@nazario.com";
  
  const isSuperAdmin = userRoleLower === "super_admin" ||
                       userEmailLower === "admin@nazario.com";

  // القوائم بالعربية فقط
  const navItems = [
    { path: "/dashboard", icon: LayoutDashboard, labelAr: "لوحة التحكم" },
    { path: "/pos", icon: ShoppingCart, labelAr: "نقطة البيع" },
    { path: "/inventory", icon: Package, labelAr: "المخزون" },
    { path: "/customers", icon: Users, labelAr: "الزبائن" },
    { path: "/suppliers", icon: Truck, labelAr: "الموردين" },
    { path: "/expenses", icon: Wallet, labelAr: "المصروفات" },
    { path: "/reports", icon: BarChart3, labelAr: "التقارير" },
    { path: "/cash-register", icon: Banknote, labelAr: "صندوق النقدية" },
    { path: "/cash-box", icon: Wallet, labelAr: "كشف الصندوق اليومي", highlight: true },
    { path: "/installments", icon: CreditCard, labelAr: "البيع بالتقسيط" },
    { path: "/overdue-report", icon: AlertTriangle, labelAr: "تقرير المتأخرين", highlight: true },
    { path: "/sales", icon: Receipt, labelAr: "المبيعات" },
    { path: "/held-invoices", icon: PauseCircle, labelAr: "الفواتير المعلقة", highlight: true },
    { path: "/purchases", icon: Truck, labelAr: "المشتريات" },
    { path: "/customer-debts", icon: CreditCard, labelAr: "ديون الزبائن" },
    { path: "/supplier-debts", icon: HandCoins, labelAr: "ديون الموردين" },
    { path: "/employees", icon: UserCog, labelAr: "الموظفين" },
    { path: "/damages", icon: AlertTriangle, labelAr: "التالفات" },
    { path: "/returns", icon: RotateCcw, labelAr: "المرتجعات" },
    { path: "/expiry-alerts", icon: AlertTriangle, labelAr: "تنبيهات الصلاحية", highlight: true },
    { path: "/backup", icon: Database, labelAr: "النسخ الاحتياطي" },
    { path: "/roles", icon: Shield, labelAr: "أدوار الموظفين" },
    { path: "/team", icon: Users, labelAr: "فريق العمل", highlight: true },
    { path: "/labels-settings", icon: Tag, labelAr: "إعدادات الملصقات" },
    { path: "/store-settings", icon: Store, labelAr: "إعدادات المحل", highlight: true },
    { path: "/account-statement", icon: FileText, labelAr: "كشف حساب", highlight: true },
    { path: "/voucher?type=receipt", icon: BookOpen, labelAr: "سند قبض", highlight: true },
    { path: "/voucher?type=payment", icon: BookOpen, labelAr: "سند صرف", highlight: true },
    { path: "/multi-vouchers", icon: BookOpen, labelAr: "السندات المتعددة", highlight: true },
    { path: "/audit-log", icon: Shield, labelAr: "سجل العمليات", highlight: true },
    { path: "/reminders", icon: Bell, labelAr: "التذكيرات", highlight: true },
    { path: "/theme", icon: Palette, labelAr: "تخصيص الألوان", highlight: true },
    { path: "/support", icon: HelpCircle, labelAr: "الدعم الفني", highlight: true },
    { path: "/settings", icon: Settings, labelAr: "الإعدادات" },
  ];

  // إضافة رابط White Label Settings إذا كان المستخدم White Label
  if (isWhiteLabel) {
    navItems.push({ path: "/whitelabel-settings", icon: Palette, labelAr: "إعدادات العلامة التجارية", special: true });
  }

  // إضافة روابط الأدمن
  if (isAdmin) {
    navItems.push({ path: "/ads-admin", icon: Megaphone, labelAr: "إدارة الإعلانات", isAdmin: true });
    navItems.push({ path: "/installment-management", icon: CreditCard, labelAr: "إدارة الأقساط", isAdmin: true });
    navItems.push({ path: "/license-admin", icon: Crown, labelAr: "إدارة التراخيص", isAdmin: true });
    navItems.push({ path: "/activation-history", icon: History, labelAr: "سجل التفعيلات", isAdmin: true });
    navItems.push({ path: "/installment-reports", icon: BarChart3, labelAr: "تقارير الأقساط", isAdmin: true });
    navItems.push({ path: "/super-admin", icon: Shield, labelAr: "Super Admin", isAdmin: true });
  }

  // إضافة لوحة المالك للـ Super Admin
  if (isSuperAdmin) {
    navItems.push({ path: "/owner-panel", icon: Shield, labelAr: "لوحة المالك", isOwner: true, highlight: true });
    navItems.push({ path: "/admin/partners", icon: Handshake, labelAr: "إدارة الشركاء", isOwner: true });
  }

  return (
    <>
      {/* Mobile Overlay — تعتيم وضبابية أعلى لتركيز المستخدم على الـ Sidebar */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-md z-40 lg:hidden animate-in fade-in duration-200"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:sticky top-0 right-0 lg:right-auto h-screen w-[85%] max-w-[300px] lg:w-64
          bg-slate-900 border-l lg:border-l-0 lg:border-r border-slate-700
          shadow-2xl shadow-black/50 lg:shadow-none
          transform transition-transform duration-300 ease-in-out z-50
          ${isOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"}
          flex flex-col
        `}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Logo - Calculator Icon */}
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-white text-lg">
                  Nazario
                </h1>
                <p className="text-emerald-500 text-xs font-semibold">
                  نظام الحسابات
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="lg:hidden text-slate-400"
            >
              <X size={20} />
            </Button>
          </div>

          {/* ✨ شريط الموظف الحالي — يظهر للموظفين فقط */}
          {user?.is_employee && (
            <div
              className="mt-3 p-2.5 rounded-lg bg-gradient-to-br from-cyan-900/40 to-blue-900/40 border border-cyan-500/30 flex items-center gap-2.5"
              data-testid="employee-badge"
            >
              <div className="w-8 h-8 rounded-full bg-cyan-500/30 flex items-center justify-center flex-shrink-0">
                <UserCircle className="w-5 h-5 text-cyan-300" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-white text-sm font-semibold truncate" data-testid="employee-name">
                  {user?.name || "موظف"}
                </p>
                <p className="text-cyan-300 text-[10px] font-medium uppercase tracking-wider">
                  {user?.role === "cashier" ? "كاشير"
                    : user?.role === "accountant" ? "محاسب"
                    : user?.role === "viewer" ? "مشاهد فقط"
                    : "موظف"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin">
          {/* إظهار نوع الترخيص */}
          <div className={`mx-3 mb-4 p-2 rounded-lg text-center text-xs font-medium ${
            isOwner || licenseType === "lifetime" || licenseType === "full"
              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
              : licenseType === "whitelabel" 
                ? "bg-purple-500/20 text-purple-400 border border-purple-500/30" 
                : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
          }`}>
            <Crown className="w-4 h-4 inline-block me-1" />
            {isOwner ? "ترخيص دائم" : getLicenseTypeName()}
          </div>
          
          <ul className="space-y-1 px-2">
            {navItems.filter((item) => isAllowedByPermission(item.path)).map((item) => {
              // المالك أو المحترف يدخل كل شيء بدون قفل
              const isLocked = !isOwner && !isProfessional && PROFESSIONAL_ONLY_PAGES.includes(item.path);
              
              if (isLocked) {
                // عنصر مقفل - يظهر لكن لا يعمل
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => promptUpgrade(item.labelAr)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 text-slate-500 hover:bg-slate-800/50 hover:text-slate-400 cursor-pointer group"
                    >
                      <item.icon size={20} className="opacity-50" />
                      <span className="font-medium opacity-50">{item.labelAr}</span>
                      <Lock size={14} className="ms-auto text-amber-500 group-hover:animate-pulse" />
                    </button>
                  </li>
                );
              }
              
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200 ${
                        item.isAdmin
                          ? isActive
                            ? "bg-amber-600 text-white shadow-lg shadow-amber-600/30"
                            : "text-amber-400 hover:bg-amber-600/20 hover:text-amber-300 border border-amber-500/30"
                          : item.special
                            ? isActive
                              ? "bg-purple-600 text-white shadow-lg shadow-purple-600/30"
                              : "text-purple-400 hover:bg-purple-600/20 hover:text-purple-300 border border-purple-500/30"
                            : item.highlight
                              ? isActive
                                ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/30"
                                : "text-emerald-400 hover:bg-emerald-600/20 hover:text-emerald-300 border border-emerald-500/30"
                              : isActive
                                ? "bg-red-600 text-white shadow-lg shadow-red-600/30"
                                : "text-slate-300 hover:bg-slate-800 hover:text-white"
                      }`
                    }
                  >
                    <item.icon size={20} />
                    <span className="font-medium">{item.labelAr}</span>
                    {/* Badge ديناميكي للفواتير المعلقة */}
                    {item.path === "/held-invoices" && heldCount > 0 && (
                      <span
                        className="ms-auto bg-red-500 text-white text-xs font-bold min-w-[22px] h-[22px] px-1.5 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30 animate-pulse"
                        data-testid="held-invoices-badge"
                      >
                        {heldCount > 99 ? "99+" : heldCount}
                      </span>
                    )}
                    {item.highlight && !isLocked && item.path !== "/held-invoices" && (
                      <span className="ms-auto bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded-full animate-pulse">
                        NEW
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer - Account Section (collapsible) */}
        <div className="p-4 border-t border-slate-700">
          <button
            type="button"
            onClick={() => setAccountOpen((v) => !v)}
            className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-slate-300 hover:bg-slate-800 transition-colors"
            data-testid="account-section-toggle"
          >
            <span className="flex items-center gap-3">
              <UserCircle size={20} />
              <span className="font-medium">الحساب</span>
            </span>
            {accountOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {accountOpen && (
            <div className="space-y-1 mt-1 ps-2 border-s border-slate-700">
              <NavLink
                to="/about"
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-all w-full text-sm ${
                    isActive
                      ? "bg-slate-700 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  }`
                }
              >
                <Info size={16} />
                <span>معلومات الحساب</span>
              </NavLink>
              <NavLink
                to="/settings"
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-all w-full text-sm ${
                    isActive
                      ? "bg-slate-700 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  }`
                }
              >
                <Settings size={16} />
                <span>إعدادات الحساب</span>
              </NavLink>
              <NavLink
                to="/security"
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-all w-full text-sm ${
                    isActive
                      ? "bg-slate-700 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  }`
                }
              >
                <Activity size={16} />
                <span>الأمان</span>
              </NavLink>
              <NavLink
                to="/privacy"
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-all w-full text-sm ${
                    isActive
                      ? "bg-slate-700 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  }`
                }
              >
                <Shield size={16} />
                <span>سياسة الخصوصية</span>
              </NavLink>
              <NavLink
                to="/terms"
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg transition-all w-full text-sm ${
                    isActive
                      ? "bg-slate-700 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  }`
                }
              >
                <CreditCard size={16} />
                <span>شروط الاستخدام</span>
              </NavLink>
              <Button
                variant="ghost"
                onClick={logout}
                className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10 text-sm h-auto py-2 px-3"
                data-testid="logout-btn"
              >
                <LogOut size={16} className="me-3" />
                تسجيل الخروج
              </Button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};
